import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  NgZone,
  OnChanges,
  OnDestroy,
  signal,
  SimpleChanges,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { translation } from '@cue/translations';
import { cueCursorFinger, IconComponent } from '@cue/ui/icons';
import { BackgroundTypeEnum, DigitalSignageConfig } from '@cue/navigation-services';
import { localized } from '@cue/translate';
import { NgStyle } from '@angular/common';
import { CdkConnectedOverlay, Overlay } from '@angular/cdk/overlay';

@Component({
  selector: 'cue-digital-signage',
  templateUrl: './digital-signage.component.html',
  styleUrls: ['./digital-signage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, NgStyle, CdkConnectedOverlay],
})
export class DigitalSignageComponent implements OnChanges, OnDestroy {
  overlay = inject(Overlay);
  globalOverlay = this.overlay.position().global().centerHorizontally().centerVertically() as any;

  backgroundTypeEnum = BackgroundTypeEnum;
  canStopDigitalSignage = input<boolean>();
  config = input.required<DigitalSignageConfig>();
  private zone = inject(NgZone);
  private translocoService = inject(TranslocoService);
  private activeIndex = signal(0);
  private accumulatedTime = 0;
  private interval?;

  videoFix = signal(true);
  effectFix = effect(() => {
    this.activeIndex();
    this.videoFix.set(false);
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.videoFix.set(true);
      });
    });
  });

  touchText = computed(() => {
    return localized(this.config().customText, this.translocoService)
      ? localized(this.config().customText, this.translocoService)
      : this.translocoService.translate(translation.wayfinding.touchMe);
  });

  activeMediumShowTime = computed(() => {
    return this.activeMedium()?.showTime * 1000;
  });

  activeMedium = computed(() => {
    const mediaList = this.config().media;

    if (mediaList?.length > 0) {
      return mediaList[this.activeIndex()];
    }
    return null;
  });

  setNextIndex() {
    const nextIndex = (this.activeIndex() + 1) % this.config().media.length;
    this.activeIndex.set(nextIndex);
  }

  private setupInterval() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    this.zone.runOutsideAngular(() => {
      this.interval = setInterval(() => {
        this.accumulatedTime += 1000;
        const activeMediumShowTime = this.activeMediumShowTime();
        if (activeMediumShowTime != null && this.accumulatedTime >= activeMediumShowTime) {
          this.accumulatedTime = 0;
          this.setNextIndex();
        }
      }, 1000);
    });
  }

  protected getSignageBackgroundStyle() {
    let result;
    const digitalSignageConfigData = this.config();

    if (digitalSignageConfigData.media == null && digitalSignageConfigData.backgroundId === BackgroundTypeEnum.Color) {
      result = {
        'background-color': digitalSignageConfigData.backgroundColor,
      };
    } else if (digitalSignageConfigData.backgroundId === BackgroundTypeEnum.Color) {
      result = {
        'background-color': this.config().backgroundColor,
      };
    } else if (digitalSignageConfigData.backgroundId === BackgroundTypeEnum.Image) {
      result = {
        'background-image': 'url(' + digitalSignageConfigData.backgroundImage + ')',
      };
    } else {
      result = {
        'background-color': 'rgba(0, 0, 0, 0.5)',
      };
    }
    return result;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.setupInterval();
    }
  }

  ngOnDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  protected translation = translation;
  protected ICONS = {
    cueCursorFinger,
  };
}
